import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { useMaintenance } from '@/contexts/MaintenanceContext';

interface MaintenancePageProps {
  className?: string;
}

export const MaintenancePage: React.FC<MaintenancePageProps> = ({ className }) => {
  const { enableBypass } = useMaintenance();
  const [clickCount, setClickCount] = useState(0);

  const handleLogoClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    
    if (newCount >= 20) {
      enableBypass();
      window.location.reload();
    }
  };

  return (
    <div
      className={cn(
        'fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background overflow-hidden',
        className
      )}
    >
      {/* Animated background glow effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Top-left gold glow */}
        <div
          className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, hsl(45 93% 58%) 0%, transparent 70%)',
            animation: 'pulse-glow 4s ease-in-out infinite',
          }}
        />
        {/* Bottom-right gold glow */}
        <div
          className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full opacity-15"
          style={{
            background: 'radial-gradient(circle, hsl(45 85% 45%) 0%, transparent 70%)',
            animation: 'pulse-glow 4s ease-in-out infinite 2s',
          }}
        />
        {/* Center subtle glow */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-10"
          style={{
            background: 'radial-gradient(circle, hsl(45 93% 58%) 0%, transparent 60%)',
          }}
        />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-lg">
        {/* Logo with glow effect - tap 5 times to enable bypass */}
        <div className="relative mb-8">
          <div
            className="absolute inset-0 blur-3xl opacity-40"
            style={{
              background: 'radial-gradient(circle, hsl(45 93% 58%) 0%, transparent 70%)',
              width: 200,
              height: 200,
              left: -40,
              top: -40,
            }}
          />
          <img
            src="/gold-machine-logo.png"
            alt="Gold Machine"
            width={120}
            height={120}
            className="relative z-10 object-contain drop-shadow-2xl select-none"
            style={{
              filter: 'drop-shadow(0 0 30px hsl(45 93% 58% / 0.4))',
            }}
            onClick={handleLogoClick}
            draggable={false}
          />
        </div>

        {/* Decorative gold line */}
        <div
          className="w-24 h-1 rounded-full mb-6"
          style={{
            background: 'linear-gradient(90deg, transparent, hsl(45 93% 58%), transparent)',
          }}
        />

        {/* Main heading */}
        <h1
          className="text-3xl sm:text-4xl font-bold mb-4 tracking-tight"
          style={{
            background: 'linear-gradient(135deg, hsl(45 93% 68%), hsl(45 93% 58%), hsl(45 85% 45%))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          We'll Be Right Back
        </h1>

        {/* Subheading */}
        <p className="text-lg text-muted-foreground mb-6">
          Gold Machine is currently undergoing scheduled maintenance to bring you an even better experience.
        </p>

        {/* Info card */}
        <div
          className="w-full p-6 rounded-2xl border border-primary/20 bg-card/50 backdrop-blur-sm"
          style={{
            boxShadow: '0 0 40px -10px hsl(45 93% 58% / 0.15)',
          }}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            {/* Animated gear icon */}
            <svg
              className="w-6 h-6 text-primary"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                animation: 'spin-slow 8s linear infinite',
              }}
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            <span className="text-sm font-medium text-primary">Maintenance in Progress</span>
          </div>
          <p className="text-sm text-muted-foreground">
            We're working hard to improve your gaming experience. Please check back soon - we won't be long!
          </p>
        </div>

        {/* Animated loading dots */}
        <div className="flex items-center gap-2 mt-8">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-2 h-2 rounded-full bg-primary"
              style={{
                animation: 'bounce-dot 1.4s infinite ease-in-out',
                animationDelay: `${i * 0.16}s`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Custom animations */}
      <style>{`
        @keyframes pulse-glow {
          0%, 100% {
            opacity: 0.15;
            transform: scale(1);
          }
          50% {
            opacity: 0.25;
            transform: scale(1.1);
          }
        }
        
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes bounce-dot {
          0%, 80%, 100% {
            transform: scale(0.6);
            opacity: 0.4;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default MaintenancePage;
